import { isLocalhost, isVercel } from 'src/utils';

/**
 * Tenant hostname resolution + per-tenant brand-color cache for the loading
 * spinner.
 *
 * The spinner arc reads the `--spinner-color` CSS var, which `Theme.store`
 * sets from the company's `primary_color` ("Main color (Dark)") once the
 * company API responds. But that response isn't available synchronously and
 * react-query's cache is in-memory (lost on hard reload), so without help the
 * arc shows the navy default for a beat on every reload, then flips to brand.
 *
 * To show the brand color from the very first frame we cache it in
 * localStorage keyed by tenant: `Theme.store` writes it on load, and
 * `src/index.tsx` reads it synchronously at startup (before first paint).
 *
 * `getCrmHostname()` mirrors the resolver in `Company.store` (same
 * `isLocalhost`/`isVercel`) so the cache key always matches the tenant the
 * company was fetched for. All `window`/`localStorage` access is guarded for
 * sandboxed embeds.
 */

export const DEFAULT_CRM_HOSTNAME = 'portal.wastehero.io';

const cacheKey = (hostname: string) => `wh-spinner-color:${hostname}`;

// Accept hex (#rgb…#rrggbbaa) or rgb()/hsl()/rgba()/hsla() — anything we'd have
// stored from a backend brand color. Rejects junk so a corrupted entry can't
// inject arbitrary CSS into the spinner.
const isColorValue = (value: string) =>
  /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value.trim()) ||
  /^(rgb|hsl)a?\([^)]*\)$/i.test(value.trim());

export function getCrmHostname(): string {
  if (window.WH_SS_PORTAL_DOMAIN) return window.WH_SS_PORTAL_DOMAIN;
  if (isVercel || isLocalhost) {
    const fromQuery = new URLSearchParams(window.location.search).get(
      'crm_hostname'
    );
    if (fromQuery) {
      try {
        localStorage.setItem('DEV_CRM_HOSTNAME', fromQuery);
      } catch {
        /* storage unavailable (sandboxed iframe) — ignore */
      }
      return fromQuery;
    }
    try {
      return localStorage.getItem('DEV_CRM_HOSTNAME') || DEFAULT_CRM_HOSTNAME;
    } catch {
      return DEFAULT_CRM_HOSTNAME;
    }
  }
  return window.location.hostname.replace(/^www\./, '');
}

/** Last-known brand color for the current tenant, or null if none/invalid. */
export function readCachedBrandColor(): string | null {
  try {
    const value = localStorage.getItem(cacheKey(getCrmHostname()));
    return value && isColorValue(value) ? value : null;
  } catch {
    return null;
  }
}

/** Persist the tenant brand color so the next reload can paint it immediately. */
export function cacheBrandColor(color: string): void {
  try {
    if (isColorValue(color)) {
      localStorage.setItem(cacheKey(getCrmHostname()), color);
    }
  } catch {
    /* storage unavailable — ignore */
  }
}

/** Forget the cached brand color (e.g. when a tenant unsets primary_color). */
export function clearCachedBrandColor(): void {
  try {
    localStorage.removeItem(cacheKey(getCrmHostname()));
  } catch {
    /* storage unavailable — ignore */
  }
}
